// Admin Daily Schedule > Create Lesson Plan. Pick the day, Balgruha, coaches
// and time; every child of the Balgruha gets a proposed plan built from
// where they are (Syllabus Tracker scores + practice, latest GCompris game).
// The admin reviews it, then saves a draft, creates the coaches' visits
// ("Create Schedule") and shares it. The left sidebar is the student Learning
// Zone's course tree: choosing a course re-proposes that column.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  CalendarDays,
  ChevronDown,
  Clapperboard,
  Clock,
  Eye,
  FileText,
  HeartPulse,
  Home,
  Info,
  Keyboard,
  Lightbulb,
  Monitor,
  Music,
  Paintbrush,
  Paperclip,
  Plus,
  Save,
  Share2,
  Trash2,
  X,
} from 'lucide-react';
import {
  createLessonPlan,
  deleteLessonPlanAttachment,
  findLessonPlans,
  getBalagruha,
  getLessonPlan,
  getLessonPlanAttachmentUrl,
  getLessonPlanChildrenProgress,
  getLessonPlanPreviousVisits,
  shareLessonPlan,
  updateLessonPlan,
  uploadLessonPlanAttachment,
} from '../../api';
import showToast from '../../utils/toast';
import loadAllUsers from '../../components/adminDailySchedule/loadAllUsers';
import useFixedHeaderOverlap from '../../components/adminDailySchedule/useFixedHeaderOverlap';
import {
  TIME_OPTIONS,
  combineDateAndTime,
  formatClock12,
  formatDateKeyLong,
  formatDateKeyWithWeekday,
  formatTimeValue12,
  isCoachUser,
  toDateKey,
  toTimeValue,
} from '../../components/adminDailySchedule/scheduleUtils';
import LessonPlanCourseSidebar from '../../components/lessonPlan/LessonPlanCourseSidebar';
import CoachChipsSelect from '../../components/lessonPlan/CoachChipsSelect';
import { PreviousVisitDialog, ShareDialog } from '../../components/lessonPlan/LessonPlanDialogs';
import {
  COMPUTER_APPS,
  DEFAULT_COMPUTER_APP,
  DEFAULT_FOCUS,
  ONE_ON_ONE_COURSES,
  computerOptions,
  findOption,
  isComputerFocus,
  isOneOnOneFocus,
  oneOnOneOptionGroups,
  proposeRow,
  refocusRow,
  rowFromSaved,
  rowToItem,
} from '../../components/lessonPlan/lessonPlanProposal';
import '../../components/lessonPlan/CreateLessonPlan.css';
import SearchableSelect from '../../components/common/SearchableSelect';

const DEFAULT_START = '10:00';
const DEFAULT_END = '11:00';
const DAILY_SCHEDULE_PATH = '/admin/daily-schedule';
const PROPOSAL_EXPLAINED =
  "Each child's next item comes from their scored Syllabus Tracker sessions and uploaded practice; " +
  "items scored below half marks are revised first. Computer uses the child's latest GCompris game.";

const computerIcon = (app) => {
  if (app === 'typing-master') return <Keyboard size={16} className="lp-icon is-sky" aria-hidden="true" />;
  if (app === 'artweaver') return <Paintbrush size={16} className="lp-icon is-orange" aria-hidden="true" />;
  if (app === 'canva') return <Paintbrush size={16} className="lp-icon is-purple" aria-hidden="true" />;
  return <Monitor size={16} className="lp-icon is-green" aria-hidden="true" />;
};

const oneOnOneIcon = (option) => {
  const course = Object.values(ONE_ON_ONE_COURSES).find(
    (item) => item.topic === option?.topic || item.label === option?.topic
  );
  if (course?.icon === 'scene') return <Clapperboard size={16} className="lp-icon is-green" aria-hidden="true" />;
  if (course?.icon === 'song') return <Music size={16} className="lp-icon is-purple" aria-hidden="true" />;
  if (course?.icon === 'story') return <BookOpen size={16} className="lp-icon is-orange" aria-hidden="true" />;
  return <FileText size={16} className="lp-icon is-muted" aria-hidden="true" />;
};

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

export default function CreateLessonPlan() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planIdParam = searchParams.get('id');
  const pageRef = useRef(null);
  const headerOverlap = useFixedHeaderOverlap(pageRef);
  const fileInputRef = useRef(null);
  const todayKey = toDateKey(new Date());

  const [centres, setCentres] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [form, setForm] = useState(() => ({
    dateKey: searchParams.get('date') || todayKey,
    balagruhaId: searchParams.get('balagruhaId') || '',
    coachIds: searchParams.get('coachId') ? [searchParams.get('coachId')] : [],
    startTime: DEFAULT_START,
    endTime: DEFAULT_END,
  }));
  const [focusView, setFocusView] = useState(DEFAULT_FOCUS);
  const [oneOnOneFocus, setOneOnOneFocus] = useState(DEFAULT_FOCUS);
  const [computerApp, setComputerApp] = useState(DEFAULT_COMPUTER_APP);
  const [progress, setProgress] = useState(null);
  const [childrenState, setChildrenState] = useState({ loading: false, error: '' });
  const [rows, setRows] = useState([]);
  const [previousVisits, setPreviousVisits] = useState([]);
  const [plan, setPlan] = useState(null);
  // A plan opened (by id or as a found draft) whose rows replace the proposal.
  const [loadedPlan, setLoadedPlan] = useState(null);
  const [extras, setExtras] = useState({ healthCheck: '', generalInstructions: '' });
  const [openExtras, setOpenExtras] = useState({ health: false, general: false, attachments: false });
  const [busy, setBusy] = useState('');
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');
  const [share, setShare] = useState(null);
  const [viewVisit, setViewVisit] = useState(null);

  const isScheduled = plan?.status === 'scheduled';
  const centre = centres.find((item) => item._id === form.balagruhaId);
  const childrenById = useMemo(
    () => new Map((progress?.children || []).map((child) => [String(child._id), child])),
    [progress]
  );
  const proposalOptions = { focusView: oneOnOneFocus, computerApp, maxScore: progress?.maxScore || 30 };

  // ---------------------------------------------------------------- loading

  useEffect(() => {
    Promise.allSettled([getBalagruha(), loadAllUsers()]).then(([centreResult, userResult]) => {
      if (centreResult.status === 'fulfilled') setCentres(centreResult.value?.data?.balagruhas || []);
      if (userResult.status === 'fulfilled') {
        setCoaches(
          userResult.value
            .filter((user) => isCoachUser(user) && user.status !== 'inactive')
            .map((user) => ({ _id: user._id, name: user.name || user.email || 'Coach' }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      }
      if (centreResult.status === 'rejected' || userResult.status === 'rejected') {
        setFormError('Could not load Balgruhas or coaches. Refresh the page to try again.');
      }
    });
  }, []);

  // Puts a saved plan into the form; its rows are merged once progress loads.
  const applyPlan = useCallback((saved) => {
    const visit = saved.schedules?.[0];
    setPlan(saved);
    setLoadedPlan(saved);
    setForm({
      dateKey: String(visit?.date || saved.date).slice(0, 10),
      balagruhaId: String(saved.balagruhaId?._id || saved.balagruhaId),
      coachIds: (saved.coachIds || []).map((coach) => String(coach?._id || coach)),
      startTime: toTimeValue(visit?.startTime || saved.startTime) || DEFAULT_START,
      endTime: toTimeValue(visit?.endTime || saved.endTime) || DEFAULT_END,
    });
    setExtras({ healthCheck: saved.healthCheck || '', generalInstructions: saved.generalInstructions || '' });
    setOpenExtras({
      health: Boolean(saved.healthCheck),
      general: Boolean(saved.generalInstructions),
      attachments: (saved.attachments || []).length > 0,
    });
    if (isOneOnOneFocus(saved.focusCourse)) {
      setFocusView(saved.focusCourse);
      setOneOnOneFocus(saved.focusCourse);
    }
  }, []);

  useEffect(() => {
    if (!planIdParam) return;
    getLessonPlan(planIdParam)
      .then((response) => applyPlan(response.data.plan))
      .catch((error) => setFormError(errorMessage(error, 'Could not open this lesson plan.')));
  }, [planIdParam, applyPlan]);

  useEffect(() => {
    if (!form.balagruhaId) {
      setProgress(null);
      return undefined;
    }
    let cancelled = false;
    setChildrenState({ loading: true, error: '' });
    getLessonPlanChildrenProgress(form.balagruhaId)
      .then((response) => !cancelled && setProgress(response.data))
      .catch((error) => {
        if (cancelled) return;
        setProgress(null);
        setChildrenState({ loading: false, error: errorMessage(error, "Could not load this Balgruha's children.") });
      })
      .finally(() => !cancelled && setChildrenState((state) => ({ ...state, loading: false })));
    return () => {
      cancelled = true;
    };
  }, [form.balagruhaId]);

  useEffect(() => {
    if (!form.balagruhaId || !form.dateKey) {
      setPreviousVisits([]);
      return undefined;
    }
    let cancelled = false;
    getLessonPlanPreviousVisits(form.balagruhaId, form.dateKey)
      .then((response) => !cancelled && setPreviousVisits(response.data.visits || []))
      .catch(() => !cancelled && setPreviousVisits([]));
    return () => {
      cancelled = true;
    };
  }, [form.balagruhaId, form.dateKey]);

  // A draft saved earlier for the same Balgruha and day is picked up again.
  useEffect(() => {
    if (planIdParam || plan || !form.balagruhaId || !form.dateKey) return undefined;
    let cancelled = false;
    findLessonPlans({ balagruhaId: form.balagruhaId, date: form.dateKey, status: 'draft' })
      .then(async (response) => {
        const draft = response?.data?.plans?.[0];
        if (!draft || cancelled) return;
        const details = await getLessonPlan(draft._id);
        if (cancelled) return;
        applyPlan(details.data.plan);
        setNotice(`Continuing the draft saved on ${new Date(draft.updatedAt).toLocaleString('en-IN')}.`);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [planIdParam, plan, form.balagruhaId, form.dateKey, applyPlan]);

  // Rows: one per child, from the opened plan where it has the child,
  // otherwise proposed from the child's progress.
  useEffect(() => {
    if (!progress) {
      setRows([]);
      return;
    }
    const savedItems =
      loadedPlan && String(loadedPlan.balagruhaId?._id || loadedPlan.balagruhaId) === String(progress.balagruha._id)
        ? new Map(loadedPlan.items.map((item) => [String(item.studentId?._id || item.studentId), item]))
        : new Map();
    setRows(
      progress.children.map((child) =>
        savedItems.has(String(child._id))
          ? rowFromSaved(savedItems.get(String(child._id)), child)
          : proposeRow(child, { focusView: oneOnOneFocus, computerApp, maxScore: progress.maxScore })
      )
    );
    // Re-proposing on focus changes is done by refocus(), keeping edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress, loadedPlan]);

  // ------------------------------------------------------------- editing

  const setField = (field, value) => {
    setFormError('');
    setForm((current) => ({ ...current, [field]: value }));
  };

  const changeBalagruha = (balagruhaId) => {
    setFormError('');
    setNotice('');
    setPlan(null);
    setLoadedPlan(null);
    setForm((current) => ({ ...current, balagruhaId }));
  };

  const refocus = (options) =>
    setRows((current) => current.map((row) => refocusRow(row, childrenById.get(String(row.studentId)), options)));

  const selectCourse = (item) => {
    const view = item.view;
    if (isOneOnOneFocus(view)) {
      setFocusView(view);
      setOneOnOneFocus(view);
      refocus({ ...proposalOptions, focusView: view });
    } else if (isComputerFocus(view)) {
      setFocusView(view);
      setComputerApp(view);
      refocus({ ...proposalOptions, computerApp: view });
    } else {
      showToast(`No ${item.label} lessons to plan yet`, 'error');
    }
  };

  const updateRow = (studentId, changes, editedField) =>
    setRows((current) =>
      current.map((row) =>
        row.studentId === studentId
          ? { ...row, ...changes, edited: { ...row.edited, [editedField]: true } }
          : row
      )
    );

  // ------------------------------------------------------------- saving

  const buildPayload = (status) => ({
    balagruhaId: form.balagruhaId,
    date: form.dateKey,
    startTime: combineDateAndTime(form.dateKey, form.startTime).toISOString(),
    endTime: combineDateAndTime(form.dateKey, form.endTime).toISOString(),
    coachIds: form.coachIds,
    focusCourse: oneOnOneFocus,
    items: rows.map(rowToItem),
    healthCheck: extras.healthCheck,
    generalInstructions: extras.generalInstructions,
    ...(status ? { status } : {}),
  });

  const validate = (forSchedule) => {
    if (!form.dateKey) return 'Please select a date.';
    if (!form.balagruhaId) return 'Please select a Balgruha.';
    if (form.coachIds.length === 0) return 'Please select at least one coach.';
    if (combineDateAndTime(form.dateKey, form.startTime) >= combineDateAndTime(form.dateKey, form.endTime)) {
      return 'End time must be after the start time.';
    }
    if (forSchedule && !isScheduled && form.dateKey < todayKey) return 'The schedule date cannot be in the past.';
    return '';
  };

  // Saves the plan; status 'scheduled' also creates the visits. Returns the plan.
  const save = async (status) => {
    const problem = validate(status === 'scheduled');
    if (problem) {
      setFormError(problem);
      return null;
    }
    setFormError('');
    const payload = buildPayload(isScheduled ? undefined : status);
    const response = plan ? await updateLessonPlan(plan._id, payload) : await createLessonPlan(payload);
    const saved = response.data.plan;
    setPlan(saved);
    setNotice('');
    return saved;
  };

  const run = async (kind, action) => {
    setBusy(kind);
    try {
      await action();
    } catch (error) {
      setFormError(errorMessage(error, 'Something went wrong. Please try again.'));
    } finally {
      setBusy('');
    }
  };

  const coachNames = (saved) => (saved.coachIds || []).map((coach) => coach.name).join(', ');

  const saveDraft = () =>
    run('draft', async () => {
      const saved = await save(isScheduled ? undefined : 'draft');
      if (saved) showToast(isScheduled ? 'Lesson plan updated' : 'Draft saved', 'success');
    });

  const createSchedule = () =>
    run('schedule', async () => {
      const saved = await save('scheduled');
      if (saved) showToast(`Schedule created for ${coachNames(saved)}`, 'success');
    });

  const shareSchedule = () =>
    run('share', async () => {
      const saved = isScheduled ? plan : await save('scheduled');
      if (!saved) return;
      const response = await shareLessonPlan(saved._id);
      setPlan(response.data.plan);
      setShare({ title: `Lesson Plan – ${centre?.name || ''}`, text: response.data.shareText });
    });

  // --------------------------------------------------------- attachments

  const addAttachment = (file) =>
    run('attach', async () => {
      const saved = plan || (await save('draft'));
      if (!saved) return;
      const response = await uploadLessonPlanAttachment(saved._id, file);
      setPlan((current) => ({ ...(current || saved), attachments: response.data.attachments }));
      showToast(`${file.name} attached`, 'success');
    });

  const openAttachment = (attachment) =>
    run('attach', async () => {
      const response = await getLessonPlanAttachmentUrl(plan._id, attachment._id);
      window.open(response.data.url, '_blank', 'noopener');
    });

  const removeAttachment = (attachment) =>
    run('attach', async () => {
      const response = await deleteLessonPlanAttachment(plan._id, attachment._id);
      setPlan((current) => ({ ...current, attachments: response.data.attachments }));
    });

  // ------------------------------------------------------------- render

  const timeOptions = (value) => (TIME_OPTIONS.includes(value) ? TIME_OPTIONS : [...TIME_OPTIONS, value].sort());
  const slotLocked = isScheduled;
  const childCount = progress?.children?.length || 0;
  const attachments = plan?.attachments || [];

  return (
    <div ref={pageRef} className="lp-page" style={{ '--lp-header-overlap': `${headerOverlap}px` }}>
      <div className="lp-layout">
        <LessonPlanCourseSidebar activeView={focusView} onSelectCourse={selectCourse} />

        <main className="lp-main">
          <header className="lp-header">
            <span className="lp-title-icon" aria-hidden="true">
              <CalendarDays size={28} />
            </span>
            <div>
              <h1>Create Lesson Plan</h1>
              <p>Plan - Teach - Track - Make an Impact</p>
            </div>
          </header>

          {notice && (
            <p className="lp-banner is-info" role="status">
              <Info size={16} aria-hidden="true" /> {notice}
            </p>
          )}
          {isScheduled && (
            <p className="lp-banner is-success" role="status">
              <CalendarDays size={16} aria-hidden="true" />
              Scheduled for {coachNames(plan)}. Change the day, time or coaches from the Daily Schedule; the children&apos;s
              learning below can still be updated.
            </p>
          )}

          <section className="lp-fields" aria-label="Visit details">
            <div className="lp-field">
              <label htmlFor="lp-date">
                Date <span className="lp-required">*</span>
              </label>
              <div className="lp-control lp-date">
                <CalendarDays size={18} className="lp-control-icon" aria-hidden="true" />
                <span>{form.dateKey ? formatDateKeyWithWeekday(form.dateKey) : 'Select date'}</span>
                <ChevronDown size={16} aria-hidden="true" />
                <input
                  id="lp-date"
                  type="date"
                  value={form.dateKey}
                  disabled={slotLocked}
                  onChange={(event) => setField('dateKey', event.target.value)}
                  onClick={(event) => {
                    try {
                      event.currentTarget.showPicker?.();
                    } catch (error) {
                      // showPicker is unsupported here; typing still works.
                    }
                  }}
                />
              </div>
            </div>

            <div className="lp-field">
              <label htmlFor="lp-balagruha">
                Balgruha <span className="lp-required">*</span>
              </label>
              <div className="lp-control">
                <Home size={18} className="lp-control-icon" aria-hidden="true" />
                <SearchableSelect
                  searchPlaceholder="Search balagruha..."
                  id="lp-balagruha"
                  value={form.balagruhaId}
                  disabled={slotLocked}
                  onChange={(event) => changeBalagruha(event.target.value)}
                >
                  <option value="">Select Balgruha</option>
                  {centres.map((item) => (
                    <option key={item._id} value={item._id}>
                      {item.name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
            </div>

            <div className="lp-field lp-field-wide">
              <label htmlFor="lp-coaches">
                Coaches <span className="lp-required">*</span>
              </label>
              <CoachChipsSelect
                id="lp-coaches"
                coaches={coaches}
                value={form.coachIds}
                onChange={(coachIds) => setField('coachIds', coachIds)}
                disabled={slotLocked}
              />
            </div>

            {[
              ['startTime', 'Start Time'],
              ['endTime', 'End Time'],
            ].map(([field, label]) => (
              <div key={field} className="lp-field">
                <label htmlFor={`lp-${field}`}>
                  {label} <span className="lp-required">*</span>
                </label>
                <div className="lp-control">
                  <Clock size={18} className="lp-control-icon" aria-hidden="true" />
                  <select
                    id={`lp-${field}`}
                    value={form[field]}
                    disabled={slotLocked}
                    onChange={(event) => setField(field, event.target.value)}
                  >
                    {timeOptions(form[field]).map((value) => (
                      <option key={value} value={value}>
                        {formatTimeValue12(value)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </section>

          <section className="lp-plan" aria-labelledby="lp-plan-title">
            <div className="lp-plan-heading">
              <h2 id="lp-plan-title">
                Proposed Lesson Plan
                {centre && (
                  <span>
                    {' '}
                    — {centre.name} ({childCount} {childCount === 1 ? 'Child' : 'Children'})
                  </span>
                )}
                <span className="lp-plan-info" role="img" aria-label={PROPOSAL_EXPLAINED} title={PROPOSAL_EXPLAINED}>
                  <Info size={18} aria-hidden="true" />
                </span>
              </h2>
              <p className="lp-plan-hint">
                <Lightbulb size={16} aria-hidden="true" />
                Select a course from the left to review or update proposed learning.
              </p>
            </div>
            <p className="lp-banner is-info">
              <Info size={16} aria-hidden="true" />
              This lesson plan is auto-generated from existing updated data in Playground. Please review and edit the
              learning plan before creating and sharing the schedule.
            </p>

            {!form.balagruhaId && <p className="lp-empty">Select a Balgruha to see its children and their proposed learning.</p>}
            {form.balagruhaId && childrenState.loading && <p className="lp-empty" role="status">Loading children...</p>}
            {childrenState.error && <p className="lp-error" role="alert">{childrenState.error}</p>}
            {progress && childCount === 0 && <p className="lp-empty">No children are registered in this Balgruha.</p>}

            {progress && childCount > 0 && (
              <div className="lp-table-wrap">
                <table className="lp-table">
                  <colgroup>
                    <col className="lp-col-num" />
                    <col className="lp-col-child" />
                    <col className="lp-col-computer" />
                    <col className="lp-col-one-on-one" />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Child Name</th>
                      <th scope="col">
                        <Monitor size={16} aria-hidden="true" /> Computer
                        <span className="lp-th-focus">
                          {COMPUTER_APPS.find((app) => app.id === computerApp)?.label}
                        </span>
                      </th>
                      <th scope="col">
                        <Eye size={16} aria-hidden="true" /> One-on-One
                        <span className="lp-th-focus">{ONE_ON_ONE_COURSES[oneOnOneFocus]?.label}</span>
                      </th>
                      <th scope="col">Special Instructions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => {
                      const child = childrenById.get(String(row.studentId));
                      const computerChoices = computerOptions(child, row.computer);
                      const oneOnOneGroups = oneOnOneOptionGroups(child, oneOnOneFocus, progress.maxScore, row.oneOnOne);
                      const allOneOnOne = oneOnOneGroups.flatMap((group) => group.options);
                      return (
                        <tr key={row.studentId}>
                          <td className="lp-num">{index + 1}</td>
                          <th scope="row" className="lp-child" title={row.name}>
                            {row.name}
                          </th>
                          <td>
                            <div className="lp-cell-control">
                              {computerIcon(row.computer?.app)}
                              <SearchableSelect
                                searchPlaceholder="Search activity..."
                                aria-label={`Computer for ${row.name}`}
                                value={findOption(computerChoices, row.computer)?.key || ''}
                                onChange={(event) =>
                                  updateRow(
                                    row.studentId,
                                    { computer: computerChoices.find((option) => option.key === event.target.value) },
                                    'computer'
                                  )
                                }
                              >
                                {computerChoices.map((option) => (
                                  <option key={option.key} value={option.key}>
                                    {option.label}
                                  </option>
                                ))}
                              </SearchableSelect>
                            </div>
                          </td>
                          <td>
                            <div className="lp-cell-control">
                              {oneOnOneIcon(row.oneOnOne)}
                              <SearchableSelect
                                searchPlaceholder="Search activity..."
                                aria-label={`One-on-One for ${row.name}`}
                                value={findOption(allOneOnOne, row.oneOnOne)?.key || ''}
                                onChange={(event) => {
                                  const option = allOneOnOne.find((item) => item.key === event.target.value);
                                  updateRow(
                                    row.studentId,
                                    {
                                      oneOnOne: option,
                                      ...(row.edited.specialInstructions || !option?.note
                                        ? {}
                                        : { specialInstructions: option.note }),
                                    },
                                    'oneOnOne'
                                  );
                                }}
                              >
                                {oneOnOneGroups.map((group) => (
                                  <optgroup key={group.label} label={group.label}>
                                    {group.options.map((option) => (
                                      <option key={option.key} value={option.key}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </optgroup>
                                ))}
                              </SearchableSelect>
                            </div>
                          </td>
                          <td>
                            <input
                              className="lp-instructions"
                              type="text"
                              maxLength={500}
                              value={row.specialInstructions}
                              title={row.specialInstructions}
                              aria-label={`Special instructions for ${row.name}`}
                              onChange={(event) =>
                                updateRow(row.studentId, { specialInstructions: event.target.value }, 'specialInstructions')
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="lp-extras" aria-label="More details">
            <div className="lp-extras-buttons">
              <button
                type="button"
                className={`lp-extra-toggle is-health${openExtras.health ? ' is-open' : ''}`}
                aria-expanded={openExtras.health}
                onClick={() => setOpenExtras((current) => ({ ...current, health: !current.health }))}
              >
                <HeartPulse size={18} aria-hidden="true" />
                <Plus size={14} aria-hidden="true" /> Health Check <span className="lp-optional">(Optional)</span>
              </button>
              <button
                type="button"
                className={`lp-extra-toggle is-general${openExtras.general ? ' is-open' : ''}`}
                aria-expanded={openExtras.general}
                onClick={() => setOpenExtras((current) => ({ ...current, general: !current.general }))}
              >
                <FileText size={18} aria-hidden="true" />
                <Plus size={14} aria-hidden="true" /> General Instructions
              </button>
              <button
                type="button"
                className={`lp-extra-toggle is-attachments${openExtras.attachments ? ' is-open' : ''}`}
                aria-expanded={openExtras.attachments}
                onClick={() => setOpenExtras((current) => ({ ...current, attachments: !current.attachments }))}
              >
                <Paperclip size={18} aria-hidden="true" />
                <Plus size={14} aria-hidden="true" /> Attachments
                {attachments.length > 0 && <span className="lp-count">{attachments.length}</span>}
              </button>
            </div>

            {openExtras.health && (
              <label className="lp-extra-panel">
                <span>Health Check (Optional)</span>
                <textarea
                  rows={2}
                  maxLength={2000}
                  value={extras.healthCheck}
                  onChange={(event) => setExtras((current) => ({ ...current, healthCheck: event.target.value }))}
                  placeholder="e.g. Check temperature of children who were unwell last week"
                />
              </label>
            )}
            {openExtras.general && (
              <label className="lp-extra-panel">
                <span>General Instructions</span>
                <textarea
                  rows={2}
                  maxLength={2000}
                  value={extras.generalInstructions}
                  onChange={(event) =>
                    setExtras((current) => ({ ...current, generalInstructions: event.target.value }))
                  }
                  placeholder="Instructions for the coaches for the whole visit"
                />
              </label>
            )}
            {openExtras.attachments && (
              <div className="lp-extra-panel">
                <span>Attachments</span>
                {attachments.length === 0 && <p className="lp-muted">No files attached yet.</p>}
                <ul className="lp-attachments">
                  {attachments.map((attachment) => (
                    <li key={attachment._id}>
                      <button type="button" className="lp-link" onClick={() => openAttachment(attachment)}>
                        <Paperclip size={14} aria-hidden="true" /> {attachment.name}
                      </button>
                      <button
                        type="button"
                        className="lp-icon-button"
                        onClick={() => removeAttachment(attachment)}
                        aria-label={`Remove ${attachment.name}`}
                        disabled={Boolean(busy)}
                      >
                        <Trash2 size={16} aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="lp-file-input"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                  aria-label="Attach a file"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) addAttachment(file);
                  }}
                />
                <button
                  type="button"
                  className="lp-button lp-button-outline is-small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={Boolean(busy)}
                >
                  <Plus size={16} aria-hidden="true" /> {busy === 'attach' ? 'Uploading...' : 'Add file'}
                </button>
                {!plan && <p className="lp-muted">The plan is saved as a draft when you attach the first file.</p>}
              </div>
            )}
          </section>

          <section className="lp-previous" aria-labelledby="lp-previous-title">
            <h2 id="lp-previous-title">
              <Clock size={18} aria-hidden="true" /> Previous Two Visits <span>(For Reference)</span>
            </h2>
            <table className="lp-previous-table">
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Coaches</th>
                  <th scope="col">Time</th>
                  <th scope="col">Key Focus</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {previousVisits.length === 0 && (
                  <tr>
                    <td colSpan={5} className="lp-muted">
                      {form.balagruhaId ? 'No earlier visits to this Balgruha.' : 'Select a Balgruha to see its last visits.'}
                    </td>
                  </tr>
                )}
                {previousVisits.map((visit) => (
                  <tr key={visit.scheduleIds[0]}>
                    <td>{formatDateKeyLong(String(visit.date).slice(0, 10))}</td>
                    <td>{visit.coaches.join(', ')}</td>
                    <td>
                      {formatClock12(visit.startTime)} - {formatClock12(visit.endTime)}
                    </td>
                    <td>{visit.keyFocus}</td>
                    <td>
                      <button type="button" className="lp-view-button" onClick={() => setViewVisit(visit)}>
                        <Eye size={14} aria-hidden="true" /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {formError && (
            <p className="lp-error" role="alert">
              {formError}
              <button type="button" className="lp-icon-button" onClick={() => setFormError('')} aria-label="Dismiss">
                <X size={16} aria-hidden="true" />
              </button>
            </p>
          )}

          <footer className="lp-footer">
            <button type="button" className="lp-back" onClick={() => navigate(DAILY_SCHEDULE_PATH)}>
              <ArrowLeft size={18} aria-hidden="true" /> Back to List
            </button>
            <div className="lp-footer-actions">
              <button type="button" className="lp-button lp-button-outline" onClick={saveDraft} disabled={Boolean(busy)}>
                <Save size={18} aria-hidden="true" />
                {busy === 'draft' ? 'Saving...' : isScheduled ? 'Save Changes' : 'Save as Draft'}
              </button>
              <button
                type="button"
                className="lp-button lp-button-primary"
                onClick={createSchedule}
                disabled={Boolean(busy) || isScheduled}
                title={isScheduled ? 'The schedule has been created' : undefined}
              >
                <CalendarDays size={18} aria-hidden="true" />
                {busy === 'schedule' ? 'Creating...' : isScheduled ? 'Schedule Created' : 'Create Schedule'}
              </button>
              <button type="button" className="lp-button lp-button-outline" onClick={shareSchedule} disabled={Boolean(busy)}>
                <Share2 size={18} aria-hidden="true" />
                {busy === 'share' ? 'Sharing...' : 'Share Schedule'}
              </button>
            </div>
          </footer>
        </main>
      </div>

      {viewVisit && <PreviousVisitDialog visit={viewVisit} onClose={() => setViewVisit(null)} />}
      {share && (
        <ShareDialog
          title={share.title}
          text={share.text}
          onClose={() => setShare(null)}
          onCopied={() => showToast('Lesson plan copied', 'success')}
          onCopyFailed={() => showToast('Could not copy; select the text and copy it instead', 'error')}
        />
      )}
    </div>
  );
}
